/**
 * Type, source, and priority chips used across the Channels UI.
 */

const TYPE_META = {
  chat:      { tone: 'border-accent/30  bg-accent-soft  text-accent',   icon: 'fa-comments',          label: 'Chat' },
  order:     { tone: 'border-warning/30 bg-warning-soft text-warning',  icon: 'fa-clipboard-list',    label: 'Order' },
  broadcast: { tone: 'border-success/30 bg-success-soft text-success',  icon: 'fa-bullhorn',          label: 'Broadcast' },
  system:    { tone: 'border-line       bg-canvas       text-mist',     icon: 'fa-gear',              label: 'System' },
};

export function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.chat;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}>
      <i className={`fa-solid ${meta.icon} text-[9px]`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

const SOURCE_META = {
  oss:    { tone: 'border-accent/20 bg-accent-soft  text-accent',  icon: 'fa-sitemap',     label: 'OSS' },
  custom: { tone: 'border-line      bg-canvas       text-mist',    icon: 'fa-shapes',      label: 'Custom' },
};

export function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.custom;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}>
      <i className={`fa-solid ${meta.icon} text-[9px]`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

const PRIORITY_META = {
  routine:   { tone: 'text-mist',                                   icon: 'fa-circle' },
  priority:  { tone: 'text-accent',                                 icon: 'fa-circle' },
  immediate: { tone: 'text-warning',                                icon: 'fa-circle' },
  flash:     { tone: 'text-rose-700 animate-pulse',                 icon: 'fa-bolt' },
};

export function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.routine;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider ${meta.tone}`}>
      <i className={`fa-solid ${meta.icon} text-[8px]`} aria-hidden="true" />
      {priority}
    </span>
  );
}

/**
 * Hot-fix #11/12 — channel data-type category. Derives chat vs map from
 * the dataTypes array on the row. Tone is purposely distinct from the
 * Type/Source/Priority badges so operators can scan the listing and
 * tell "what kind of payload does this carry" at a glance.
 *   chat — accent palette (familiar "messaging" association)
 *   map  — emerald palette (situational / map-data association)
 */
const CHAT_TOKENS = new Set(['text', 'audio_notes', 'images', 'files']);
const MAP_TOKENS  = new Set([
  'cop_layers', 'awds_layers', 'cot_layers', 'nffi_layers', 'adatp3_layers',
  'location', 'intel', 'targets',
]);

export function DataCategoryBadge({ dataTypes }) {
  if (!Array.isArray(dataTypes) || dataTypes.length === 0) return null;
  const isChat = dataTypes.some((t) => CHAT_TOKENS.has(t));
  const isMap  = dataTypes.some((t) => MAP_TOKENS.has(t));
  if (isChat) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent"
        title={`Chat content: ${dataTypes.join(', ')}`}
      >
        <i className="fa-solid fa-comments text-[9px]" aria-hidden="true" />
        Chat
      </span>
    );
  }
  if (isMap) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-emerald-300/40 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700"
        title={`Map / situational data: ${dataTypes.join(', ')}`}
      >
        <i className="fa-solid fa-map-location-dot text-[9px]" aria-hidden="true" />
        Map
      </span>
    );
  }
  return null;
}

/**
 * Hot-fix #10. Shown on channels (or persons) with home_node_only=true so
 * operators see at a glance that this row is roaming-restricted. Tone uses
 * the warning-soft palette for "attention but not error".
 */
export function HomeOnlyBadge({ active, label = 'home only' }) {
  if (!active) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning"
      title="Restricted to the unit's effective home node — won't roam."
    >
      <i className="fa-solid fa-lock text-[9px]" aria-hidden="true" />
      {label}
    </span>
  );
}

export function ScopeBadge({ scope }) {
  if (!scope) return null;
  const tone =
    scope === 'subtree'
      ? 'border-accent/30 bg-accent-soft text-accent'
      : 'border-line bg-canvas text-mist';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
      <i className={`fa-solid ${scope === 'subtree' ? 'fa-folder-tree' : 'fa-folder'} text-[9px]`} aria-hidden="true" />
      {scope}
    </span>
  );
}
